import React, { memo, useState, useMemo } from 'react';
import { Layout, Menu, Card, Space, Typography, Row, Col, Input, Checkbox, Button, Divider, Select, DatePicker, message } from 'antd';
import { ShoppingCartOutlined, DeleteOutlined, UserOutlined, BankOutlined, ReloadOutlined, DollarOutlined, SearchOutlined } from '@ant-design/icons';
import './Sales.css';
const { Text, Title } = Typography;
const { Sider, Content } = Layout;
const { Option } = Select;


function POSLayout({
  students,
  items,
  filteredItems,
  selectedStudent,
  setSelectedStudent,
  cartItems,
  setCartItems,
  updateQuantity,
  removeFromCart,
  discount,
  handleDiscountChange,
  vatChecked,
  handleVatChange,
  subtotal,
  vat,
  total,
  selectedCategory,
  setSelectedCategory,
  categories,
  formatPrice,
  paymentMethod,
  handlePaymentMethodChange,
  selectedBank,
  setSelectedBank,
  bankDetails,
  loadingBanks,
  handlePurchase,
  isPurchaseDisabled,
  handleRefresh,
  addToCart,
  note,
  setNote,
  paymentDate,
  setPaymentDate
}) {

  const [searchQuery, setSearchQuery] = useState('');


  // Filter items by both category and search query
const displayedItems = useMemo(() => {
  const query = searchQuery.toLowerCase();

  return filteredItems.filter(item =>
    [
      item.item_name,
      item.item_description,
      item.category,
      item.product_id?.toString(),
    ].some(
      field =>
        typeof field === 'string' &&
        field.toLowerCase().includes(query)
    )
  );
}, [filteredItems, searchQuery]);


  return (
    <div className="pos-content-wrapper">
      <Layout className="pos-system">

        {/* CATEGORY SIDEBAR */}
        <Sider width={200} className="category-sider">
          <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
            <Button icon={<ReloadOutlined />} type="primary" onClick={handleRefresh}>Refresh & Clear</Button>
          </div>

          <Menu
            mode="inline"
            selectedKeys={[selectedCategory]}
            onClick={({ key }) => setSelectedCategory(key)}
            items={categories.map(cat => ({ key: cat, label: cat }))}
          />
        </Sider>

        {/* ITEMS CONTENT */}
        <Content className="items-content">
          {/* Search Bar */}
          <div style={{ marginBottom: 16 }}>
            <Input
              placeholder="Search items by name, description, or category..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              allowClear
              size="large"
            />
          </div>

          <Row gutter={[16, 16]}>
            {displayedItems.length > 0 ? (
              displayedItems.map(item => (
                <Col xs={24} sm={12} md={8} lg={6} key={item.inventory_id}>
                  <Card
                    hoverable
                    className="item-card"
                    onClick={() => addToCart(item)}
                    cover={
                      <div style={{ height: 120, background: '#fafafa', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ShoppingCartOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
                      </div>
                    }
                  >
                    <Text strong>{item.item_name}</Text>
                    <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                       {item.product_id}
                    </Text>

                    <Text strong type="success" style={{ display: 'block', marginTop: 8 }}>
                      {formatPrice(item.price_per_unit)} THB
                    </Text>
                  </Card>
                </Col>
              ))
            ) : (
              <Col span={24}>
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Text type="secondary">No items found matching your search</Text>
                </div>
              </Col>
            )}
          </Row>
        </Content>

        {/* CART */}
        <Sider width={350} className="cart-sider">
          <Card className="cart-card">
            <div className="cart-content">

              {/* SELECT STUDENT */}
             <Select
                showSearch
                placeholder="Select Customer"
                value={selectedStudent}
                onChange={setSelectedStudent}
                suffixIcon={<UserOutlined />}
                filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                >
                {students.map(student => (
                    <Option
                    key={student.userid}
                    value={student.userid}
                    label={`${student.nickname} ${student.firstname} ${student.lastname}`}
                    >
                    {student.nickname} {student.firstname} {student.lastname}
                    </Option>
                ))}
                </Select>


              {/* CART ITEMS */}
              <div className="cart-items">
                {cartItems.map(item => (
                  <div key={item.inventory_id} className="cart-item">
                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Text>{item.item_name}</Text>

                      <Input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={e => updateQuantity(item.inventory_id, parseInt(e.target.value))}
                        style={{ width: 60 }}
                      />

                      <Text strong>{formatPrice(item.price_per_unit * item.quantity)} THB</Text>

                      <Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={() => removeFromCart(item.inventory_id)} />
                    </Space>
                  </div>
                ))}
              </div>

              <Divider />

              {/* SUMMARY */}
              <Space direction="vertical" style={{ width: '100%' }}>
                <div className="summary-line">
                  <Text>Subtotal</Text>
                  <Text>{formatPrice(subtotal)} THB</Text>
                </div>

                <div className="summary-line">
                  <Text>Discount</Text>
                  <Input value={discount} onChange={handleDiscountChange} style={{ width: 100 }} suffix="THB" />
                </div>

                <div className="summary-line">
                  <Checkbox checked={vatChecked} onChange={handleVatChange}>VAT (7%)</Checkbox>
                  <Text>{formatPrice(vat)} THB</Text>
                </div>

                <div className="summary-line total">
                  <Title level={4}>Total</Title>
                  <Title level={4}>{formatPrice(total)} THB</Title>
                </div>
                {/* NOTE FIELD */}
                <div> <Text>Note</Text></div>
                    <div className="summary-line">
                   
                    <Input.TextArea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        />
                    </div>


                {/* PAYMENT METHOD */}
                <Select placeholder="Payment Method" value={paymentMethod} onChange={handlePaymentMethodChange} style={{ width: '100%' }}>
                  <Option value="cash">Cash</Option>
                  <Option value="bank_transfer">Bank Transfer</Option>
                  <Option value="credit_card">Credit Card</Option>
                  <Option value="unpaid">❗ Unpaid</Option>
                </Select>

                {/* BANK SELECTION */}
                {paymentMethod === 'bank_transfer' && (
                  <Select
                    placeholder="Choose bank..."
                    onChange={setSelectedBank}
                    value={selectedBank}
                    loading={loadingBanks}
                  >
                    {bankDetails.map(b => (
                      <Option key={b.id} value={b.id}>
                        {b.bank_name} ({b.account_number})
                      </Option>
                    ))}
                  </Select>
                )}

                {/* PAYMENT DATE — optional, and an unpaid sale has none yet.
                    Clear it to record the sale without a payment date and set it
                    later from Change Payment Method in the Sales Report. */}
                {paymentMethod !== 'unpaid' && (
                  <>
                    <div><Text>Payment Date <Text type="secondary">(optional)</Text></Text></div>
                    <DatePicker
                      value={paymentDate}
                      onChange={setPaymentDate}
                      format="DD MMM YYYY"
                      placeholder="No payment date"
                      style={{ width: '100%' }}
                    />
                  </>
                )}

                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<DollarOutlined />}
                  onClick={handlePurchase}
                  disabled={isPurchaseDisabled()}
                >
                  Process Payment
                </Button>
              </Space>
            </div>
          </Card>
        </Sider>
      </Layout>
    </div>
  );
}

export default memo(POSLayout);